import type { InsightsSnapshot } from './context'

/**
 * Captures each registered snapshot sequentially and packages the PNGs into a
 * single ZIP. Sequential capture avoids mounting several offscreen 1080×1080
 * React trees at once; any capture rejection propagates so the caller can abort
 * the whole download rather than ship a partial archive.
 */
export async function buildSnapshotsZip(
  snapshots: InsightsSnapshot[],
): Promise<Blob> {
  // Loaded on demand so jszip stays out of the Insights route bundle and is
  // only fetched when the user actually triggers a download.
  const { default: JSZip } = await import('jszip')
  const zip = new JSZip()
  const captureAtIndex = async (index: number): Promise<void> => {
    const snapshot = snapshots[index]
    if (!snapshot) return

    const blob = await snapshot.getBlob()
    zip.file(snapshot.filename, blob)
    await captureAtIndex(index + 1)
  }

  await captureAtIndex(0)
  return zip.generateAsync({ type: 'blob' })
}

/**
 * Downloads a Blob via a transient object-URL anchor. The URL is revoked on the
 * next tick — revoking synchronously can cancel the download before the browser
 * has started reading the blob.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
